/**
 * components/landing/home/WhiteLabel.jsx — Section 9.
 *
 * The White-Label story with a 5-stop onboarding timeline that reveals
 * as the section enters view.  Left column: value narrative + "you
 * bring / we bring" split.  Right column: an animated tenant brand
 * preview showing brand tokens, custom domain and email templating.
 */

import { useRef } from 'react'
import clsx from 'clsx'
import { Container, Eyebrow, SectionHeading, GradientButton, GhostButton } from '../design-system'
import { useInView, MockDashboardChrome } from './_shared'
import { CheckCircle2, Building2, Palette, Globe, Settings2, Rocket, ChevronRight } from 'lucide-react'

const KALKI_BRINGS = [
  'Platform', 'Infrastructure', 'Hosting', 'Updates', 'Security',
  'AI', 'Trading', 'Academy', 'Subscriptions', 'Automation',
]
const YOU_BRING = [
  'Your brand', 'Your domain', 'Your customers', 'Your operations',
]
const TIMELINE = [
  { icon: Building2, title: 'Choose license',       body: 'Quarterly / Half-year / Annual.  All plans include the full stack.' },
  { icon: Palette,   title: 'Configure branding',  body: 'Colors, logo, wordmark, email templates, favicon.' },
  { icon: Globe,     title: 'Connect domain',      body: 'Bring your own domain — automatic SSL + CDN.' },
  { icon: Settings2, title: 'Configure platform',  body: 'Modules, plans, risk limits, exchanges, providers.' },
  { icon: Rocket,    title: 'Launch',              body: 'Invite your team and open the door to your customers.' },
]

const WhiteLabel = () => {
  const ref = useRef(null)
  const inView = useInView(ref)

  return (
    <section ref={ref} className="py-24 md:py-40 relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 -z-10 opacity-40"
        style={{ background: 'radial-gradient(50% 40% at 20% 20%, rgba(255,193,7,0.08), transparent 70%), radial-gradient(50% 40% at 80% 80%, rgba(16,185,129,0.06), transparent 70%)' }} />
      <Container>
        <div className="grid gap-14 lg:grid-cols-[1fr,1.1fr] items-center">
          <div className="space-y-6">
            <Eyebrow>White-Label Platform</Eyebrow>
            <SectionHeading
              title={<>Launch your own AI trading platform<br /><span className="text-brand-gold">without building from scratch</span>.</>}
              subtitle="Kalki Ai Cloud carries the platform.  You carry the brand and the customer relationship.  Everything else is done."
            />

            {/* You bring / we bring */}
            <div className="grid gap-3 sm:grid-cols-2 mt-4">
              <div className="rounded-2xl border border-brand-gold/20 bg-brand-gold/[0.04] p-4">
                <div className="text-[10px] uppercase tracking-widest text-brand-gold font-semibold mb-2">Kalki Ai Cloud brings</div>
                <div className="flex flex-wrap gap-1">
                  {KALKI_BRINGS.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1 rounded-full border border-brand-gold/25 bg-brand-gold/10 text-brand-gold px-2 py-0.5 text-[11px] font-medium">
                      <CheckCircle2 className="h-3 w-3" /> {b}
                    </span>
                  ))}
                </div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">You bring</div>
                <div className="flex flex-wrap gap-1">
                  {YOU_BRING.map((b) => (
                    <span key={b} className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/[0.03] text-zinc-200 px-2 py-0.5 text-[11px] font-medium">
                      <CheckCircle2 className="h-3 w-3 text-brand-emerald" /> {b}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 pt-2">
              <GradientButton href="/white-label">Explore White-Label</GradientButton>
              <GhostButton href="/contact">Talk to us</GhostButton>
            </div>
          </div>

          {/* Tenant preview + timeline */}
          <div className="space-y-6">
            <MockDashboardChrome innerClass="p-5">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-brand-gold to-[#FFE17A] flex items-center justify-center text-black font-bold text-sm">A</div>
                  <div>
                    <div className="text-white text-sm font-semibold">Acme Trading</div>
                    <div className="text-[10px] font-mono text-zinc-500">platform.acme.com</div>
                  </div>
                </div>
                <div className="inline-flex items-center gap-1.5 rounded-full border border-brand-emerald/25 bg-brand-emerald/10 px-2 py-0.5 text-[10px] font-semibold text-brand-emerald uppercase tracking-widest">
                  <span className="h-1.5 w-1.5 rounded-full bg-brand-emerald animate-pulse" />Live
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2 text-[10px]">
                <div><div className="h-8 rounded bg-brand-gold" /><div className="mt-1 font-mono text-zinc-400">--primary</div></div>
                <div><div className="h-8 rounded bg-brand-emerald" /><div className="mt-1 font-mono text-zinc-400">--success</div></div>
                <div><div className="h-8 rounded bg-white" /><div className="mt-1 font-mono text-zinc-400">--text</div></div>
                <div><div className="h-8 rounded bg-black border border-white/10" /><div className="mt-1 font-mono text-zinc-400">--bg</div></div>
              </div>
              <div className="mt-3 rounded-lg bg-white/[0.02] p-3 space-y-2">
                <div className="text-[10px] uppercase tracking-widest text-zinc-500">Welcome email template</div>
                <div className="text-[11px] text-zinc-200 leading-relaxed">
                  Welcome to <span className="text-brand-gold font-semibold">Acme Trading</span>. Your workspace is ready…
                </div>
              </div>
            </MockDashboardChrome>

            {/* Timeline */}
            <div className="relative">
              <div aria-hidden="true" className="absolute left-5 top-4 bottom-4 w-px bg-white/5" />
              <div aria-hidden="true" className={clsx('absolute left-5 top-4 w-px bg-gradient-to-b from-brand-gold to-brand-emerald transition-all duration-[2000ms] ease-out', inView ? 'bottom-4 opacity-100' : 'bottom-[95%] opacity-0')} />
              <div className="space-y-4">
                {TIMELINE.map((s, i) => (
                  <div
                    key={s.title}
                    className={clsx(
                      'relative pl-14 opacity-0 translate-y-3 transition-all duration-700',
                      inView && 'opacity-100 translate-y-0',
                    )}
                    style={{ transitionDelay: inView ? `${i * 200}ms` : '0ms' }}
                  >
                    <div className="absolute left-0 top-0 h-11 w-11 rounded-xl border border-brand-gold/30 bg-black flex items-center justify-center text-brand-gold z-10">
                      <s.icon className="h-5 w-5" />
                    </div>
                    <div className="text-sm font-semibold text-white flex items-center gap-2">
                      {s.title}
                      <ChevronRight className="h-3.5 w-3.5 text-zinc-600" />
                    </div>
                    <div className="text-[12px] text-zinc-400 mt-1 leading-relaxed">{s.body}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  )
}

export default WhiteLabel
